import type { PostHog } from "posthog-js";
import { createContext, useContext, useEffect, useState } from "react";
import { env } from "../../env";

// Context for PostHog instance (null until initialized client-side)
const PostHogContext = createContext<PostHog | null>(null);

export function usePostHog() {
	return useContext(PostHogContext);
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
	const [client, setClient] = useState<PostHog | null>(null);

	useEffect(() => {
		// Initialize PostHog client-side only (avoids SSR issues)
		const apiKey = env.VITE_POSTHOG_API_KEY;
		if (!apiKey) return;

		import("posthog-js").then((mod) => {
			const posthog = mod.default;
			posthog.init(apiKey, {
				api_host: env.VITE_POSTHOG_HOST,
				ui_host: "https://us.posthog.com",
				capture_pageview: true,
				capture_pageleave: true,
				autocapture: true,
			});
			setClient(posthog);
		});
	}, []);

	return <PostHogContext.Provider value={client}>{children}</PostHogContext.Provider>;
}

// Hook to identify user when auth state changes
export function usePostHogIdentify(
	user: { id: number; email: string; created_at?: string | null } | null
) {
	const ph = usePostHog();
	const [identified, setIdentified] = useState(false);

	useEffect(() => {
		if (!ph) return;

		if (user && !identified) {
			ph.identify(String(user.id), {
				email: user.email,
				...(user.created_at && { created_at: user.created_at }),
			});
			setIdentified(true);
		} else if (!user && identified) {
			ph.reset();
			setIdentified(false);
		}
	}, [ph, user, identified]);
}
